/**
 * Client-side receipt compression.
 *
 * A phone photo is 3-12MB. Uploading that over hotel wifi is the worst part of
 * the whole experience, so we downscale and re-encode before the presigned
 * PUT -- which also sidesteps HEIC, because re-encoding produces JPEG.
 *
 * Runs on the main thread here for simplicity of wiring; the heavy decode is
 * already off-thread inside createImageBitmap. If a 48MP phone ever makes this
 * janky, move the whole function into workers/compress.worker.ts unchanged.
 */
import {
  QUALITY,
  THUMB_EDGE,
  THUMB_QUALITY,
  targetSizeFor,
} from '@d2p-expenses/shared';
import { applyOrientation, browserAlreadyRotated, readExifOrientation } from './exif';

export interface CompressedImage {
  full: Blob;
  thumb: Blob | null;
  width: number;
  height: number;
  exifOrientation: number | null;
  /** False when we had to fall back to the original bytes (e.g. HEIC). */
  compressed: boolean;
  needsServerTranscode: boolean;
  originalType: string;
  originalSize: number;
}

export async function compressReceipt(file: File): Promise<CompressedImage> {
  const originalType = file.type || 'application/octet-stream';
  const originalSize = file.size;

  // PDFs pass straight through: there is nothing to downscale, and a PDF
  // receipt from a vendor is already small.
  if (originalType === 'application/pdf') {
    return {
      full: file, thumb: null, width: 0, height: 0, exifOrientation: null,
      compressed: false, needsServerTranscode: false, originalType, originalSize,
    };
  }

  const exifOrientation = await readExifOrientation(file);

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // HEIC on Chrome/Firefox desktop and some Android paths throws here.
    // Second chance: an <img> decode, which Safari manages for HEIC.
    try {
      bitmap = await decodeViaImgElement(file);
    } catch {
      // Store the original untouched rather than blocking the submission on a
      // codec. The server records needsServerTranscode and the UI says so
      // plainly -- it must never fail silently.
      return {
        full: file, thumb: null, width: 0, height: 0, exifOrientation,
        compressed: false, needsServerTranscode: true, originalType, originalSize,
      };
    }
  }

  try {
    const raw = await rawDimensions(file, bitmap);
    const alreadyRotated = exifOrientation
      ? browserAlreadyRotated(exifOrientation, bitmap.width, bitmap.height, raw.width, raw.height)
      : true;

    const needsManualRotation = !alreadyRotated && exifOrientation != null && exifOrientation !== 1;
    const quarterTurn = needsManualRotation && exifOrientation >= 5 && exifOrientation <= 8;

    // For a quarter turn the output canvas has width and height swapped.
    const sourceW = quarterTurn ? bitmap.height : bitmap.width;
    const sourceH = quarterTurn ? bitmap.width : bitmap.height;
    const target = targetSizeFor(sourceW, sourceH);

    const full = await drawAndEncode(bitmap, target, {
      quality: QUALITY,
      orientation: needsManualRotation ? exifOrientation : null,
      quarterTurn,
    });

    const thumbTarget = scaleWithin(target.width, target.height, THUMB_EDGE);
    const thumb = await drawAndEncode(bitmap, thumbTarget, {
      quality: THUMB_QUALITY,
      orientation: needsManualRotation ? exifOrientation : null,
      quarterTurn,
    });

    return {
      full, thumb, width: target.width, height: target.height, exifOrientation,
      compressed: true, needsServerTranscode: false, originalType, originalSize,
    };
  } finally {
    bitmap.close();
  }
}

async function drawAndEncode(
  bitmap: ImageBitmap,
  target: { width: number; height: number },
  opts: { quality: number; orientation: number | null; quarterTurn: boolean },
): Promise<Blob> {
  const canvas = makeCanvas(target.width, target.height);
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
  if (!ctx) throw new Error('Could not get a 2D canvas context');

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  if (opts.orientation) {
    applyOrientation(ctx, opts.orientation, target.width, target.height);
  }

  // Halve repeatedly rather than one big downscale: a single large-ratio
  // drawImage aliases badly on a 4000px photo, which shows up as smeared
  // receipt text and directly costs OCR accuracy later.
  const stepped = await steppedDownscale(bitmap, opts.quarterTurn ? target.height : target.width,
                                          opts.quarterTurn ? target.width : target.height);

  const drawW = opts.quarterTurn ? target.height : target.width;
  const drawH = opts.quarterTurn ? target.width : target.height;
  ctx.drawImage(stepped, 0, 0, drawW, drawH);
  if (stepped !== bitmap && 'close' in stepped) (stepped as ImageBitmap).close();

  return encode(canvas, opts.quality);
}

/** Repeatedly halve while we are more than 2x away from the target. */
async function steppedDownscale(
  bitmap: ImageBitmap,
  targetW: number,
  targetH: number,
): Promise<ImageBitmap | HTMLCanvasElement | OffscreenCanvas> {
  let current: ImageBitmap | HTMLCanvasElement | OffscreenCanvas = bitmap;
  let w = bitmap.width;
  let h = bitmap.height;

  while (w / 2 >= targetW && h / 2 >= targetH && w > 2 && h > 2) {
    const nextW = Math.max(1, Math.floor(w / 2));
    const nextH = Math.max(1, Math.floor(h / 2));
    const canvas = makeCanvas(nextW, nextH);
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
    if (!ctx) break;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(current as CanvasImageSource, 0, 0, nextW, nextH);
    if (current !== bitmap && 'close' in current) (current as ImageBitmap).close?.();
    current = canvas;
    w = nextW;
    h = nextH;
  }

  return current;
}

function makeCanvas(width: number, height: number): HTMLCanvasElement | OffscreenCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

async function encode(canvas: HTMLCanvasElement | OffscreenCanvas, quality: number): Promise<Blob> {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type: 'image/jpeg', quality });
  }
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas encoding failed'))),
      'image/jpeg',
      quality,
    );
  });
}

function scaleWithin(width: number, height: number, maxEdge: number) {
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** Raw stored dimensions, used to infer whether the browser auto-rotated. */
async function rawDimensions(file: File, fallback: ImageBitmap) {
  try {
    const unoriented = await createImageBitmap(file, { imageOrientation: 'none' });
    const dims = { width: unoriented.width, height: unoriented.height };
    unoriented.close();
    return dims;
  } catch {
    return { width: fallback.width, height: fallback.height };
  }
}

function decodeViaImgElement(file: File): Promise<ImageBitmap> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      createImageBitmap(img)
        .then(resolve)
        .catch(reject)
        .finally(() => URL.revokeObjectURL(url));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image could not be decoded'));
    };
    img.src = url;
  });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
